/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"

// import IncomeBreakDownTabelData from "Views/Report/IncomeBreakDown/data/IncomeBreakDownTableData"

// import IncomeOverTimeTableData from "Views/Report/IncomeOverTime/data/IncomeOverTimeTableData"
import { useState, useEffect, useMemo } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import ArgonButton from "components/ArgonButton";
import { getIncomeOverTime } from "services/financeService";
import { toast } from "react-toastify";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Tables() {
  const getPeriodDateRange = (period) => {
    switch (period) {
      case "daily":
        return {
          from: dayjs().subtract(6, "day"),
          to: dayjs(),
        };
      case "monthly":
        return {
          from: dayjs().subtract(11, "month"),
          to: dayjs(),
        };
      case "quarterly":
        return {
          from: dayjs().subtract(2, "year"),
          to: dayjs(),
        };
      case "yearly":
        return {
          from: dayjs().subtract(4, "year"),
          to: dayjs(),
        };
      default:
        return {
          from: dayjs().subtract(11, "month"),
          to: dayjs(),
        };
    }
  };

  const getTutorSalary = (item) => Number(item?.salary || 0);
  const getMargin = (item) => Number(item?.totalSales || 0) - getTutorSalary(item);

  // Generate dynamic table data from incomeData
  const generateTableData = (data) => {
    const columns = [
      { name: "sno", align: "center" },
      { name: "Month", align: "left" },
      { name: "Total Sales", align: "left" },
      { name: "Tutor Salary", align: "center" },
      { name: "Margin", align: "center" },
    ];

    const rows = data.map((item, index) => ({
      sno: (
        <ArgonTypography variant="caption" fontWeight="medium" color="text">
          {index + 1}
        </ArgonTypography>
      ),
      Month: (
        <ArgonTypography variant="caption" fontWeight="medium" color="text">
          {formatPeriod(item.period)}
        </ArgonTypography>
      ),
      "Total Sales": (
        <ArgonTypography variant="caption" color="success" fontWeight="medium">
          ₹{Number(item.totalSales || 0).toLocaleString("en-IN")}
        </ArgonTypography>
      ),
      "Tutor Salary": (
        <ArgonTypography variant="caption" color="info" fontWeight="medium">
          ₹{getTutorSalary(item).toLocaleString("en-IN")}
        </ArgonTypography>
      ),
      Margin: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹{getMargin(item).toLocaleString("en-IN")}
        </ArgonTypography>
      ),
    }));

    return { columns, rows };
  };

  // Format period display
  const formatPeriod = (period) => {
    if (currentPeriod === "daily") {
      return dayjs(period).format("DD MMM YYYY");
    } else if (currentPeriod === "monthly") {
      const [year, month] = period.split("-");
      return dayjs(`${year}-${month}-01`).format("MMM YYYY");
    } else if (currentPeriod === "quarterly") {
      const [year, quarter] = period.split("-Q");
      return `Q${quarter} ${year}`;
    } else if (currentPeriod === "yearly") {
      return period;
    }
    return period;
  };

  const tabsData = [
    { label: "Last 7 day", content: "" },
    { label: "Monthly", content: "" },
    { label: "Quarterly", content: "" },
    { label: "Yearly", content: "" },
  ];

  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState("daily");
  const [activeTabLabel, setActiveTabLabel] = useState("Last 7 day");
  const initialRange = getPeriodDateRange("daily");
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);

  const { columns, rows } = useMemo(() => generateTableData(incomeData), [incomeData]);

  // Fetch income data
  const fetchIncomeData = async (
    period = currentPeriod,
    customFromDate = fromDate,
    customToDate = toDate
  ) => {
    setLoading(true);
    try {
      // Set limit based on period
      let limit = 12;
      if (period === "daily") limit = 7;
      else if (period === "monthly") limit = 12;
      else if (period === "quarterly") limit = 8;
      else if (period === "yearly") limit = 5;

      const params = {
        period: period,
        limit: limit,
      };

      // Add date range if custom dates are selected
      if (customFromDate && customToDate) {
        params.from = customFromDate.format("YYYY-MM-DD");
        params.to = customToDate.format("YYYY-MM-DD");
      }

      const response = await getIncomeOverTime(params);
      if (response.success) {
        setIncomeData(response.data || []);
      } else {
        toast.error(response.message || "Failed to fetch income data");
        setIncomeData([]);
      }
    } catch (error) {
      console.error("Error fetching income data:", error);
      toast.error("Failed to fetch income data");
      setIncomeData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tabLabel) => {
    let period = "monthly";
    let newFromDate = fromDate;
    let newToDate = toDate;

    switch (tabLabel) {
      case "Last 7 day":
        period = "daily";
        newFromDate = getPeriodDateRange("daily").from;
        newToDate = getPeriodDateRange("daily").to;
        break;
      case "Monthly":
        period = "monthly";
        newFromDate = getPeriodDateRange("monthly").from;
        newToDate = getPeriodDateRange("monthly").to;
        break;
      case "Quarterly":
        period = "quarterly";
        newFromDate = getPeriodDateRange("quarterly").from;
        newToDate = getPeriodDateRange("quarterly").to;
        break;
      case "Yearly":
        period = "yearly";
        newFromDate = getPeriodDateRange("yearly").from;
        newToDate = getPeriodDateRange("yearly").to;
        break;
      default:
        period = "monthly";
        newFromDate = getPeriodDateRange("monthly").from;
        newToDate = getPeriodDateRange("monthly").to;
    }

    setCurrentPeriod(period);
    setActiveTabLabel(tabLabel);
    setFromDate(newFromDate);
    setToDate(newToDate);
    fetchIncomeData(period, newFromDate, newToDate);
  };

  // Handle filter button click
  const handleFilter = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both From and To dates");
      return;
    }
    if (fromDate.isAfter(toDate)) {
      toast.error("From date cannot be after To date");
      return;
    }
    fetchIncomeData(currentPeriod, fromDate, toDate);
  };

  const handleResetFilters = () => {
    const defaultRange = getPeriodDateRange(currentPeriod);
    setFromDate(defaultRange.from);
    setToDate(defaultRange.to);
    fetchIncomeData(currentPeriod, defaultRange.from, defaultRange.to);
  };

  const hasFilterChanges = useMemo(() => {
    const defaultRange = getPeriodDateRange(currentPeriod);
    if (!fromDate || !toDate) return false;

    return (
      !fromDate.isSame(defaultRange.from, "day") || !toDate.isSame(defaultRange.to, "day")
    );
  }, [currentPeriod, fromDate, toDate]);

  const chartData = useMemo(
    () => ({
      labels: incomeData.map((item) => formatPeriod(item.period)),
      datasets: [
        {
          label: "Total Sales",
          data: incomeData.map((item) => Number(item.totalSales || 0)),
          backgroundColor: "rgba(129, 195, 255, 0.7)",
          borderColor: "rgba(129, 195, 255, 1)",
          borderWidth: 1,
        },
        {
          label: "Tutor Salary",
          data: incomeData.map((item) => getTutorSalary(item)),
          backgroundColor: "rgba(94, 200, 227, 0.7)",
          borderColor: "rgba(94, 200, 227, 1)",
          borderWidth: 1,
        },
        {
          label: "Margin",
          data: incomeData.map((item) => getMargin(item)),
          backgroundColor: "rgba(255, 228, 163, 0.7)",
          borderColor: "rgba(255, 228, 163, 1)",
          borderWidth: 1,
        },
      ],
    }),
    [incomeData, currentPeriod]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += "₹" + Number(context.parsed.y || 0).toLocaleString("en-IN");
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return "₹" + Number(value).toLocaleString("en-IN");
          },
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
        },
      },
    },
  };

  useEffect(() => {
    fetchIncomeData("daily", dayjs().subtract(6, "day"), dayjs());
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Income Over Time
              </ArgonTypography>

              <ArgonBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: "36px",
                  mb: 2,
                }}
              >
                <ArgonBox sx={{ flex: "0 1 auto", minWidth: 250, maxWidth: 400 }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    onTabChange={handleTabChange}
                    activeTab={activeTabLabel}
                  />
                </ArgonBox>
                <ArgonBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    justifyContent: "flex-end",
                    flex: "0 1 auto",
                    minWidth: 250,
                    maxWidth: 400,
                  }}
                >
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    width={150}
                    maxDate={dayjs()} // future date disable
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    width={150}
                    maxDate={dayjs()} // future date disable
                    minDate={fromDate} // to date should be after from date
                  />
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginLeft: "10px" }}
                    onClick={handleFilter}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Filter"}
                  </ArgonButton>
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleResetFilters}
                    disabled={!hasFilterChanges || loading}
                  >
                    Reset
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
              {loading && (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={4}>
                  <ArgonTypography variant="h6">Loading income data...</ArgonTypography>
                </ArgonBox>
              )}
              {!loading && incomeData.length > 0 && (
                <ArgonBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <Table columns={columns} rows={rows} />
                </ArgonBox>
              )}
              {!loading && incomeData.length > 0 && (
                <ArgonBox p={3}>
                  <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                    Income Over Time Chart
                  </ArgonTypography>
                  <ArgonBox sx={{ height: "400px", position: "relative" }}>
                    <Bar data={chartData} options={chartOptions} />
                  </ArgonBox>
                </ArgonBox>
              )}
              {!loading && incomeData.length === 0 && (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={4}>
                  <ArgonTypography variant="h6" color="textSecondary">
                    No income data available for the selected period
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
